
import React from "react";
import { StrategyProvider, StrategyWithDetails } from "@/types/strategy-types";
import { StrategiesTableProvider, useStrategiesTable } from "./strategies-table/StrategiesTableProvider";
import TableFilters from "./strategies-table/TableFilters";
import StrategiesDataTable from "./strategies-table/StrategiesDataTable";
import TablePagination from "./strategies-table/TablePagination";
import StrategiesDialogs from "./strategies-table/StrategiesDialogs";
import { sortStrategies, ITEMS_PER_PAGE } from "./strategies-table/tableUtils";
import { useToast } from "@/components/ui/use-toast";

interface StrategiesTableProps {
  strategies: StrategyWithDetails[];
  providers: StrategyProvider[];
  onToggleStatus: (strategyId: string, currentStatus: boolean) => Promise<void>;
  onCreateStrategy: (strategy: Partial<StrategyWithDetails>) => Promise<void>;
  onUpdateStrategy: (id: string, strategy: Partial<StrategyWithDetails>) => Promise<void>;
  onDeleteStrategy: (id: string) => Promise<void>;
  onToggleActive: (strategyId: string, currentActive: boolean) => Promise<void>;
  onApproveStrategy: (strategyId: string, approved: boolean) => Promise<void>;
  userRole?: string | null;
}

const StrategiesTable = ({ 
  strategies, 
  providers,
  onToggleStatus,
  onCreateStrategy,
  onUpdateStrategy,
  onDeleteStrategy,
  onToggleActive,
  onApproveStrategy,
  userRole
}: StrategiesTableProps) => {
  const { toast } = useToast();

  // Wrap delete logic to add toast
  const handleDeleteStrategy = async (id: string) => {
    try {
      await onDeleteStrategy(id);
      toast({
        title: "Strategy deleted",
        description: "The strategy has been deleted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete strategy. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <StrategiesTableProvider>
      <StrategiesTableContent 
        strategies={strategies}
        providers={providers}
        onToggleStatus={onToggleStatus}
        onCreateStrategy={onCreateStrategy}
        onUpdateStrategy={onUpdateStrategy}
        onDeleteStrategy={handleDeleteStrategy}
        onToggleActive={onToggleActive}
        onApproveStrategy={onApproveStrategy}
        userRole={userRole}
      />
    </StrategiesTableProvider>
  );
};

// Internal component to use the context
const StrategiesTableContent = ({
  strategies,
  providers,
  onToggleStatus,
  onCreateStrategy,
  onUpdateStrategy,
  onDeleteStrategy,
  onToggleActive,
  onApproveStrategy,
  userRole
}: StrategiesTableProps) => {
  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    approvalFilter,
    setApprovalFilter,
    providerFilter,
    setProviderFilter,
    currentPage,
    setCurrentPage,
    setIsCreateDialogOpen,
    sortField,
    sortOrder,
    columnFilters
  } = useStrategiesTable();

  // Filter strategies based on current filters
  const filteredStrategies = strategies.filter((strategy) => {
    const matchesSearch = strategy.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "pro" && (strategy.plan === 'pro' || strategy.plan === 'premium')) || 
      (statusFilter === "free" && strategy.plan === 'free');
    
    const matchesApproval = approvalFilter === "all" ||
      (approvalFilter === "approved" && strategy.approved) ||
      (approvalFilter === "pending" && !strategy.approved);
    
    const matchesProvider = providerFilter === "all" || 
      strategy.strategy_provider_id === providerFilter;
    
    const matchesColumnFilters = 
      (!columnFilters.name || strategy.name?.toLowerCase().includes(columnFilters.name.toLowerCase())) &&
      (!columnFilters.exchange || strategy.exchange?.toLowerCase().includes(columnFilters.exchange.toLowerCase())) &&
      (!columnFilters.minInvestment || strategy.min_investment?.toString().includes(columnFilters.minInvestment)) &&
      (!columnFilters.riskLevel || strategy.risk_level?.toString() === columnFilters.riskLevel);
    
    return matchesSearch && matchesStatus && matchesApproval && matchesProvider && matchesColumnFilters;
  });

  const sortedStrategies = sortStrategies(filteredStrategies, sortField, sortOrder);
  const totalPages = Math.ceil(sortedStrategies.length / ITEMS_PER_PAGE);

  // Only show provider filter for admins
  const filteredProviders = userRole === 'strategy_provider' 
    ? providers.filter(p => p.id === strategies[0]?.strategy_provider_id)
    : providers;

  return (
    <div className="space-y-4">
      <TableFilters 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        approvalFilter={approvalFilter}
        onApprovalFilterChange={setApprovalFilter}
        providerFilter={providerFilter}
        onProviderFilterChange={setProviderFilter}
        onCreateClick={() => setIsCreateDialogOpen(true)}
        providers={filteredProviders}
      />

      <StrategiesDataTable
        strategies={strategies}
        onToggleStatus={onToggleStatus}
        onToggleActive={onToggleActive}
        onApproveStrategy={onApproveStrategy}
        userRole={userRole}
      />

      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      <StrategiesDialogs
        providers={filteredProviders}
        onCreateStrategy={onCreateStrategy}
        onUpdateStrategy={onUpdateStrategy}
        onDeleteStrategy={onDeleteStrategy}
        userRole={userRole}
      />
    </div>
  );
};

export default StrategiesTable;
